import React, { useState, useEffect, useCallback } from 'react';
import SignalGauge from '../recession/SignalGauge';
import SignalTimeSeries from '../recession/SignalTimeSeries';
import ScoreDistributions from '../recession/ScoreDistributions';
import SourceBreakdown from '../recession/SourceBreakdown';
import TopArticles from '../recession/TopArticles';

const API_BASE = '/api';

const COLORS = {
  bg: '#0d1117',
  card: '#161b22',
  border: '#30363d',
  text: '#c9d1d9',
  textMuted: '#8b949e',
  green: '#3fb950',
  amber: '#d29922',
  red: '#f85149',
  blue: '#58a6ff',
};

const signalColor = (signal) => {
  if (signal === 'BUY') return COLORS.green;
  if (signal === 'SELL') return COLORS.red;
  return COLORS.amber;
};

const scoreColor = (score) => {
  if (score > 0.02) return COLORS.green;
  if (score < -0.02) return COLORS.red;
  return COLORS.textMuted;
};

const timeAgo = (timestamp) => {
  if (!timestamp) return 'N/A';
  const diff = Date.now() - new Date(timestamp + 'Z').getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const formatReasoningAsHtml = (text) => {
  if (!text) return '';
  return text
    .replace(/## (.+)/g, '<div style="font-size:18px;font-weight:700;color:#c9d1d9;margin:16px 0 8px">$1</div>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#c9d1d9">$1</strong>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
};


const NAV_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'distributions', label: 'Sentiment' },
  { id: 'sources', label: 'Sources' },
  { id: 'articles', label: 'Articles' },
];

export default function RecessionMonitor() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/accounts/recession-monitor/`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    document.title = '2026 Recession Monitor | UnsupervisedBias';
    return () => { document.title = 'UnsupervisedBias'; };
  }, []);

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const current = data?.current;

  const pageStyle = {
    background: COLORS.bg,
    minHeight: '100vh',
    color: COLORS.text,
    fontFamily: "'Manrope', -apple-system, sans-serif",
    padding: '40px 20px 80px',
  };

  if (error && !data) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', paddingTop: 100 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠</div>
          <div style={{ color: COLORS.red, fontSize: 18 }}>Unable to load recession monitor data</div>
          <div style={{ color: COLORS.textMuted, marginTop: 8 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Sticky section nav */}
        <nav style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: `${COLORS.bg}ee`,
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${COLORS.border}`,
          marginBottom: 24,
          padding: '10px 8px',
          display: 'flex',
          gap: 4,
        }}>
          {NAV_SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              style={{
                flex: 1,
                background: activeSection === s.id ? COLORS.card : 'transparent',
                border: `1px solid ${activeSection === s.id ? COLORS.border : 'transparent'}`,
                color: activeSection === s.id ? COLORS.text : COLORS.textMuted,
                borderRadius: 6,
                padding: '6px 4px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                letterSpacing: 0.3,
                whiteSpace: 'nowrap',
              }}
            >
              {s.label}
            </button>
          ))}
        </nav>

        {/* ═══ OVERVIEW SECTION ═══ */}
        <div id="section-overview">
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h1 style={{ fontSize: 'clamp(22px, 5.5vw, 36px)', fontWeight: 800, margin: 0, letterSpacing: '-0.5px' }}>
              2026 Recession Monitor
            </h1>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: '8px 0 24px' }}>
              Real-time market signal analysis.
            </p>

            {current && (
              <>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 16,
                  background: COLORS.card,
                  border: `2px solid ${signalColor(current.signal)}`,
                  borderRadius: 12,
                  padding: '16px 32px',
                  marginBottom: 16,
                }}>
                  <span style={{
                    fontSize: 40,
                    fontWeight: 900,
                    color: signalColor(current.signal),
                    letterSpacing: 2,
                  }}>
                    {current.signal}
                  </span>
                  <span style={{
                    fontSize: 28,
                    fontFamily: 'monospace',
                    color: scoreColor(current.composite_score),
                  }}>
                    [{current.composite_score?.toFixed(2)}]
                  </span>
                </div>

                <div className="signal-meta" style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 20, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Last updated: {timeAgo(current.timestamp)} &middot; {current.article_count} articles from {current.source_count} source{current.source_count !== 1 ? 's' : ''}
                </div>

              </>
            )}

            {!current && !error && (
              <div style={{ color: COLORS.textMuted, padding: 60 }}>Loading signal data...</div>
            )}
          </div>

          {/* Signal Gauge — right under the badge */}
          {current && (
            <SignalGauge
              composite_score={current.composite_score}
              article_count={current.article_count}
              signal={current.signal}
            />
          )}

          {/* Signal Time Series — right under the gauge */}
          <SignalTimeSeries flip_log={data?.flip_log} />

          {/* Reasoning excerpt — expandable */}
          {current?.opus_reasoning && (
            <div style={{
              background: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 8,
              padding: '16px 24px',
              maxWidth: 800,
              margin: '0 auto 32px',
              textAlign: 'left',
              fontSize: 14,
              lineHeight: 1.7,
              color: COLORS.textMuted,
            }}>
              <div dangerouslySetInnerHTML={{ __html:
                summaryExpanded
                  ? current.opus_reasoning
                      .replace(/## (.+)/g, '<div style="font-size:15px;font-weight:700;color:#c9d1d9;margin:14px 0 6px">$1</div>')
                  : current.opus_reasoning.substring(0, 400).replace(/## /g, '')
              }} />
              {!summaryExpanded && current.opus_reasoning.length > 400 && (
                <span style={{ color: COLORS.textMuted }}>... </span>
              )}
              {current.opus_reasoning.length > 400 && (
                <button
                  onClick={() => setSummaryExpanded(!summaryExpanded)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#58a6ff',
                    cursor: 'pointer',
                    fontSize: 13,
                    padding: '4px 0',
                    display: 'block',
                    marginTop: 8,
                  }}
                >
                  {summaryExpanded ? '▲ Show less' : '▼ Read full analysis'}
                </button>
              )}
            </div>
          )}


        </div>

        {/* ═══ DISTRIBUTIONS SECTION ═══ */}
        <div id="section-distributions">
          <ScoreDistributions score_distributions={data?.score_distributions} />
        </div>

        {/* ═══ SOURCES SECTION ═══ */}
        <div id="section-sources">
          <SourceBreakdown
            source_breakdown={data?.source_breakdown}
            tier_comparison={data?.tier_comparison}
          />
        </div>

        {/* ═══ ARTICLES SECTION ═══ */}
        <div id="section-articles">
          <TopArticles
            top_articles={data?.top_articles}
            source_coverage={data?.source_coverage}
          />
        </div>

        {/* Full reasoning rendered inline via the excerpt block above */}

        {/* Footer note */}
        <div style={{
          textAlign: 'center',
          color: COLORS.textMuted,
          fontSize: 12,
          padding: '20px 0',
          borderTop: `1px solid ${COLORS.border}`,
        }}>
          Signals refresh hourly. Not financial advice.
        </div>
      </div>
    </div>
  );
}
